import * as React from 'react'
import * as ReactDomServer from 'react-dom/server'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'

import * as duvet from '@ulule/duvet'
import * as hooks from '@owl-nest/hooks'
import { t, tc, tn } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import * as plume from '@ulule/owl-kit-components/next'
import * as services from '@owl-nest/services'

import { useDisplayMode } from '../hooks/useDisplayMode'
import * as UFE from '../UFE'

type AboutProps = {
  project: models.project.ProjectForOwner
}

export function About({ project }: AboutProps): React.ReactElement {
  const displayMode = useDisplayMode(project)
  const isEditMode = displayMode === 'edit'
  const isMember = models.project.isUserMember(project)
  const isClientSide = hooks.useClientSide()

  const { query: userProjectsQuery, response: userProjectsResponse } = services.user.project.useUserProjects(
    project.owner.id,
    {
      fetchOnMount: false,
      params: {
        limit: 5,
        sort: 'created_at',
        state: 'online',
      },
    },
  )
  const description = React.useMemo(() => models.project.description(project), [])
  const isMobile = React.useMemo(() => isClientSide && plume.utils.isMobile(), [isClientSide])

  const { ref: aboutUsRef, inView: isAboutUsInView } = useInView({
    rootMargin: '40px 0px 0px 0px',
    triggerOnce: true,
  })

  React.useEffect(() => {
    if (
      userProjectsResponse.status === hooks.QueryStatus.PRISTINE &&
      project.show_created_projects &&
      isAboutUsInView
    ) {
      userProjectsQuery()
    }
  }, [project, isAboutUsInView])

  return (
    <AboutLayout isMember={isMember} id="about" className="membership-section" ref={aboutUsRef}>
      <AboutHeader>
        <plume.styles.heading.XS asDesktop="L">
          {tc('[highlight: About] us', {
            highlight: <plume.HighlightedTitle />,
          })}
        </plume.styles.heading.XS>
        {isEditMode && (
          <a href={`/projects/${project.id}/backoffice/#about-us`}>
            <plume.EditButton glyph={<plume.glyphs.stroke.Edit size={18} />}>{t('Edit')}</plume.EditButton>
          </a>
        )}
      </AboutHeader>

      {description !== undefined && description !== '' ? (
        isMobile ? (
          <plume.WysiwygContent
            suppressHydrationWarning
            dangerouslySetInnerHTML={{ __html: description ?? '' }}
            textSize="small"
          />
        ) : (
          <plume.WysiwygContent suppressHydrationWarning dangerouslySetInnerHTML={{ __html: description ?? '' }} />
        )
      ) : (
        <plume.WysiwygContent dangerouslySetInnerHTML={{ __html: getDescriptionPlaceholder() }} />
      )}

      {project.show_created_projects &&
        userProjectsResponse.status === hooks.QueryStatus.SUCCESS &&
        userProjectsResponse.data.projects.length > 1 && (
          <>
            <hr />
            <AboutProjects>
              <plume.styles.heading.XXXS asDesktop="M">
                {t("%(owner)s's projects", {
                  owner: project.owner.screenname,
                })}
              </plume.styles.heading.XXXS>
              <ProjectList>
                {userProjectsResponse.data.projects
                  .filter((siblingProject) => project.id !== siblingProject.id)
                  .slice(0, 4)
                  .map((siblingProject) => (
                    <duvet.CardTypeSelector
                      cardType="classic"
                      key={siblingProject.id}
                      overrideIsProjectOnline={Boolean(siblingProject.is_online)}
                      project={siblingProject}
                      rates={UFE.RATES}
                      targetCurrency={UFE.CURRENCIES.CURRENT}
                      projectTranslation={{
                        addToMyList: t('Add to my list'),
                        backed: t('Backed'),
                        backerCount: tn(
                          '%s contribution',
                          '%s contributions',
                          (siblingProject && siblingProject.supporters_count) || 0,
                          plume.accounting.formatNumber(siblingProject.orders_count || siblingProject.supporters_count),
                        ),
                        comingSoon: t('Online shortly, sign up now!'),
                        created: t('Created'),
                        extraTime: t('Extension'),
                        finished: t('Finished'),
                        highlights: t('Highlights'),
                        listed: t('Listed'),
                        progressInfo: t('%(percent)s reached', {
                          percent: `${plume.accounting.formatNumber(models.project.progress(siblingProject))}%`,
                        }),
                        shortCountdown: models.project.getCountDown(siblingProject, true),
                        status: models.project.getStatus(siblingProject),
                      }}
                    />
                  ))}
              </ProjectList>

              <plume.ButtonAsLink href={project.owner.absolute_url} kind="primary" size="small">
                {t('See all projects')}
              </plume.ButtonAsLink>
            </AboutProjects>
          </>
        )}

      <AboutOwnerWrapper>
        <duvet.AboutOwner project={project} />
        {isEditMode && (
          <EditButtonWrapper href={`/projects/${project.id}/backoffice/#profile`}>
            <plume.EditButton glyph={<plume.glyphs.stroke.Edit size={18} />}>{t('Edit')}</plume.EditButton>
          </EditButtonWrapper>
        )}
      </AboutOwnerWrapper>
    </AboutLayout>
  )

  function getDescriptionPlaceholder(): string {
    const text = (
      <>
        <h2>{t('Who am I and what type of content do I produce?')}</h2>
        <ol>
          <li>
            {t(
              'Start with a personal introduction, where you mention your name or pseudonym and give an overview of your background.',
            )}
          </li>
          <li>
            {t(
              'Clearly explain the type of content you create, describing the type of work you do and the subjects or themes you cover.',
            )}
          </li>
          <li>
            {t('Highlight what makes your work unique, talking about your passion and your creative perspective.')}
          </li>
        </ol>
        <h2>{t('Why should you support me?')}</h2>
        <ol>
          <li>
            {t(
              'Highlight the value and impact of your content, explaining how your work enriches, educates, entertains or makes a significant contribution to your community.',
            )}
          </li>
          <li>
            {t(
              'Highlight the exclusive benefits supporters will receive, such as access to never-before-seen content, direct interactions with you, and behind-the-scenes glimpses into your creative process.',
            )}
          </li>
          <li>
            {t(
              'Explain how the financial support helps you to realise your projects, improve the quality of your work, and develop new ideas, thus creating a symbiotic relationship between you and your community of supporters.',
            )}
          </li>
        </ol>
      </>
    )

    return ReactDomServer.renderToStaticMarkup(text)
  }
}

const AboutLayout = styled.div<{ isMember?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 516px;
  padding-inline: 16px;
  margin-inline: auto;
  margin-top: 40px;
  margin-bottom: 0;

  hr {
    border: 0;
    border-top: 1px solid ${plume.COLORS.BORDER_PRIMARY};
    margin-bottom: 24px;
    margin-top: calc(24px - 14px);
    width: 100%;

    @media screen and ${plume.BREAKPOINTS.TABLET} {
      margin-block-start: calc(26px - 8px);
      margin-block-end: calc(56px - 8px);
    }
  }

  ${plume.WysiwygContent} {
    margin-bottom: 32px;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin-top: 80px;
    margin-bottom: ${(props) => (props.isMember ? '10px' : '-80px')};
    padding: 0 0 10px;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    margin-bottom: ${(props) => (props.isMember ? '10px' : '-140px')};
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP_L} {
    margin-bottom: ${(props) => (props.isMember ? '10px' : '-180px')};
  }
`

const AboutHeader = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  position: relative;

  a {
    position: relative;
    top: 16px;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    a {
      position: absolute;
      right: -140px;
      top: 0;
    }
  }
`

const AboutProjects = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  margin-bottom: 24px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    gap: 24px;
    margin-bottom: 58px;
  }

  ${plume.ButtonAsLink} {
    align-self: center;
  }
`
const EditButtonWrapper = styled.a``
const AboutOwnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;

  ${EditButtonWrapper} {
    text-align: center;
  }

  ${plume.EditButton} {
    position: relative;
    top: -24px;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    ${plume.EditButton} {
      position: absolute;
      left: 300px;
      top: 0px;
    }
  }
`

const ProjectList = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(250px, 1fr));
  grid-template-rows: 1fr;
  gap: 8px;
  width: 100%;
  overflow: auto;
  scroll-snap-type: x mandatory;
  padding: 0 20px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    padding: 0;

    ${plume.styles.card.project.ProjectCardWrapper} {
      min-width: 245px;
      max-width: 245px;
      width: 245px;
    }
  }

  & > a {
    scroll-snap-align: center;
    height: 233px;
    width: 248px;
  }
`
